import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener(
      'pointerleave',
      () => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.#debouncedDeactivate.cancel();
          this.#deactivate();
        } else {
          this.#debouncedDeactivate();
        }
      },
      { signal: this.#abortController.signal }
    );

    this.addEventListener('keydown', this.#onKeyDown, {
      signal: this.#abortController.signal,
    });

    this.addEventListener('click', this.#onMenuItemClick, {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {{ target: EventTarget | null }} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {{ target: EventTarget | null }} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      if (previouslyActiveItem.hasAttribute('aria-controls')) previouslyActiveItem.ariaExpanded = 'false';
      const prevSubmenu = findSubmenu(previouslyActiveItem);
      if (prevSubmenu) {
        prevSubmenu.setAttribute('aria-hidden', 'true');
        prevSubmenu.inert = true;
      }
    }

    this.#state.activeItem = item;
    if (item.hasAttribute('aria-controls')) item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (submenu) {
      submenu.removeAttribute('aria-hidden');
      submenu.inert = false;
    }

    const moreButton = this.querySelector('[slot="more"] button');
    if (moreButton) moreButton.setAttribute('aria-expanded', (!isDefaultSlot).toString());

    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    // Focus moved somewhere inside the same <li> (e.g. into the submenu) — don't deactivate
    if (event.relatedTarget instanceof Element && event.target.closest('li')?.contains(event.relatedTarget)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.#debouncedDeactivate.cancel();
        this.#deactivate();
      } else {
        this.#debouncedDeactivate();
      }
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    if (item.hasAttribute('aria-controls')) item.ariaExpanded = 'false';

    const submenu = findSubmenu(item);
    if (submenu) {
      submenu.setAttribute('aria-hidden', 'true');
      submenu.inert = true;
    }

    const moreButton = this.querySelector('[slot="more"] button');
    if (moreButton) moreButton.setAttribute('aria-expanded', 'false');

    item.setAttribute('data-animating', '');

    const motionOk = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const animDelay = motionOk ? Math.max(0, this.animationDelay - 150) : 0;

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, animDelay);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Handle keyboard navigation for the desktop mega menu.
   * - Escape: close open submenu and return focus to the trigger link
   * - ArrowDown: move focus to the first focusable element in the open submenu
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    const activeMenuItem = this.#state.activeItem;
    const moreButton = this.querySelector('[slot="more"] button');
    const isMoreFocused = document.activeElement === moreButton;

    if (event.key === 'Escape') {
      if (!activeMenuItem) return;
      event.preventDefault();
      this.#deactivate();
      activeMenuItem.focus();
      return;
    }

    if (event.key === 'Tab' && !event.shiftKey) {
      if (!activeMenuItem || activeMenuItem.ariaExpanded !== 'true') return;
      const parentLi = activeMenuItem.closest('li');
      const submenu = parentLi?.querySelector('.menu-list__submenu');
      if (!submenu || submenu.contains(document.activeElement)) return;
      const menuItems = [...this.querySelectorAll('[ref="menuitem"]')];
      const nextItem = menuItems[menuItems.indexOf(activeMenuItem) + 1];
      if (!nextItem) return;
      event.preventDefault();
      if (nextItem instanceof HTMLElement) nextItem.focus();
      return;
    }

    if (event.key === 'ArrowDown' || (isMoreFocused && (event.key === 'Enter' || event.key === ' '))) {
      event.preventDefault();

      if (isMoreFocused) {
        const firstOverflowItem = this.querySelector(
          '[slot="overflow"] a[href]:not([tabindex="-1"]), [slot="overflow"] button:not([disabled]):not([tabindex="-1"])'
        );
        if (firstOverflowItem instanceof HTMLElement) {
          firstOverflowItem.focus();
          return;
        }
      }

      // If no submenu is open yet, open the focused item's submenu and enter it
      if (!activeMenuItem || activeMenuItem.ariaExpanded !== 'true') {
        const focusedMenuitem = document.activeElement?.closest('[ref="menuitem"]');
        if (focusedMenuitem instanceof HTMLElement && focusedMenuitem.hasAttribute('aria-controls')) {
          const submenuId = focusedMenuitem.getAttribute('aria-controls');
          const submenu = submenuId ? this.querySelector(`#${submenuId}`) : null;
          const firstFocusable = submenu?.querySelector(
            'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"])'
          );
          this.activate({ target: focusedMenuitem });
          if (firstFocusable instanceof HTMLElement) {
            // Wait one tick for the debounced activate to run and remove `inert`
            setTimeout(() => firstFocusable.focus(), 0);
          }
        }
        return;
      }

      const parentLi = activeMenuItem.closest('li');
      const firstFocusable = parentLi?.querySelector(
        '.menu-list__submenu a[href]:not([tabindex="-1"]), .menu-list__submenu button:not([disabled]):not([tabindex="-1"])'
      );
      if (firstFocusable instanceof HTMLElement) firstFocusable.focus();
    }
  };

  /**
   * Handle clicks on menu items:
   * - Chevron button (mouse-only): toggle submenu without navigating
   * - Button menuitem (Case 1, no URL): toggle submenu
   * - Link menuitem (Case 2, URL + submenu): keyboard Enter (detail===0) opens submenu;
   *   second Enter navigates naturally
   * @param {MouseEvent} event
   */
  #onMenuItemClick = (event) => {
    if (!(event.target instanceof Element)) return;

    // Chevron button (tabindex="-1", mouse-only toggle)
    const chevron = event.target.closest('.menu-list__link-toggle');
    if (chevron instanceof HTMLButtonElement) {
      const menuitem = chevron.closest('li')?.querySelector('[ref="menuitem"]');
      if (menuitem instanceof HTMLElement) {
        if (menuitem.ariaExpanded === 'true') {
          this.#debouncedActivateHandler.cancel();
          this.#deactivate();
        } else {
          this.activate({ target: chevron });
        }
      }
      return;
    }

    // Button menuitem (Case 1: no URL, button controls submenu)
    const button = event.target.closest('button[ref="menuitem"]');
    if (button instanceof HTMLButtonElement) {
      if (button.ariaExpanded === 'true') {
        this.#debouncedActivateHandler.cancel();
        this.#deactivate();
      } else {
        this.activate(event);
      }
      return;
    }

    // Link menuitem (Case 2: URL + submenu) — keyboard Enter only (detail === 0)
    // First Enter opens submenu; second Enter navigates (default behavior)
    const link = event.target.closest('a[ref="menuitem"][aria-controls]');
    if (link instanceof HTMLAnchorElement && event.detail === 0 && link.ariaExpanded !== 'true') {
      event.preventDefault();
      this.activate(event);
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  const li = element.closest('li');
  if (li?.matches('[slot="more"]')) {
    return findMenuItem(li.parentElement?.querySelector('[slot="overflow"]'));
  }
  if (!li) return null;
  const menuitem = li.querySelector('[ref="menuitem"]');
  return menuitem instanceof HTMLElement ? menuitem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
